import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Mail, CheckCircle, XCircle, Loader2 } from 'lucide-react'
import { apiClient } from '@/services/api/client'

export function VerifyEmailPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token')

  const [status, setStatus] = useState<'verifying' | 'success' | 'error' | 'no-token'>('verifying')
  const [errorMessage, setErrorMessage] = useState<string>('')

  useEffect(() => {
    const verifyEmail = async () => {
      if (!token) {
        setStatus('no-token')
        return
      }

      try {
        await apiClient.post('/api/v1/auth/verify_email', { token })
        setStatus('success')

        // Redirect to login after 3 seconds
        setTimeout(() => {
          navigate('/auth/login')
        }, 3000)
      } catch (error: any) {
        setStatus('error')
        setErrorMessage(error.response?.data?.error || 'Verification failed. Please try again.')
      }
    }

    verifyEmail()
  }, [token, navigate])

  if (status === 'verifying') {
    return (
      <div className="w-full">
        <Card className="shadow-lg">
          <CardHeader className="space-y-1">
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 bg-aviation-100 rounded-full flex items-center justify-center">
                <Loader2 className="w-8 h-8 text-aviation-600 animate-spin" />
              </div>
            </div>
            <CardTitle className="text-2xl font-bold text-center">
              {t('auth.verifying', 'Verifying Email...')}
            </CardTitle>
            <CardDescription className="text-center">
              {t('auth.verifyingDescription', 'Please wait while we verify your email address')}
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    )
  }

  if (status === 'success') {
    return (
      <div className="w-full">
        <Card className="shadow-lg">
          <CardHeader className="space-y-1">
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
            </div>
            <CardTitle className="text-2xl font-bold text-center">
              {t('auth.emailVerified', 'Email Verified!')}
            </CardTitle>
            <CardDescription className="text-center">
              {t('auth.emailVerifiedDescription', 'Your account has been successfully verified')}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-slate-600 text-center">
              {t('auth.redirectingToLogin', 'Redirecting to login page...')}
            </p>
            <Button
              onClick={() => navigate('/auth/login')}
              className="w-full"
            >
              {t('auth.goToLogin', 'Go to Login')}
            </Button>
          </CardContent>
        </Card>
      </div>
    )
  }

  if (status === 'error') {
    return (
      <div className="w-full">
        <Card className="shadow-lg">
          <CardHeader className="space-y-1">
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">
                <XCircle className="w-8 h-8 text-red-600" />
              </div>
            </div>
            <CardTitle className="text-2xl font-bold text-center">
              {t('auth.verificationFailed', 'Verification Failed')}
            </CardTitle>
            <CardDescription className="text-center text-red-600">
              {errorMessage}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Button
              onClick={() => navigate('/auth/login')}
              className="w-full"
            >
              {t('auth.backToLogin', 'Back to Login')}
            </Button>
          </CardContent>
        </Card>
      </div>
    )
  }

  // no-token state
  return (
    <div className="w-full">
      <Card className="shadow-lg">
        <CardHeader className="space-y-1">
          <div className="flex justify-center mb-4">
            <div className="w-16 h-16 bg-aviation-100 rounded-full flex items-center justify-center">
              <Mail className="w-8 h-8 text-aviation-600" />
            </div>
          </div>
          <CardTitle className="text-2xl font-bold text-center">
            {t('auth.verifyEmail', 'Verify Your Email')}
          </CardTitle>
          <CardDescription className="text-center">
            {t('auth.verifyEmailDescription', "We've sent you a verification email")}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-slate-600 text-center">
            {t('auth.checkInbox', 'Please check your inbox and click the verification link to activate your account.')}
          </p>
          <p className="text-sm text-slate-500 text-center">
            {t('auth.didntReceiveEmail', "Didn't receive the email? Check your spam folder or contact support.")}
          </p>
          <Button
            onClick={() => navigate('/auth/login')}
            className="w-full"
          >
            {t('auth.backToLogin', 'Back to Login')}
          </Button>
        </CardContent>
      </Card>
    </div>
  )
}

export default VerifyEmailPage
